<template>
    <article class="cta-block-container">
        <div
            class="cta-block"
            :class="{
                'cta-block--shadow': props.shadow,
            }"
            :style="style"
        >
            <div class="cta-block__main">
                <div
                    v-if="props.heading || props.subheading"
                    class="cta-block__main-content"
                >
                    <WatsonAtomDynamicText
                        :text="props.heading"
                        :typography="props.typography.heading"
                        class="cta-block__main-heading"
                    />
                    <WatsonAtomDynamicText
                        :text="props.subheading"
                        :typography="props.typography.subheading"
                        class="cta-block__main-subheading"
                    />
                </div>

                <ul
                    v-if="items.length"
                    class="cta-block__list"
                >
                    <li
                        v-for="(item, index) in items"
                        :key="index"
                        class="cta-block__item"
                    >
                        <WatsonAtomImage
                            v-if="item.image"
                            :content="item.image"
                            class="cta-block__item-image"
                        />
                        <!-- eslint-disable vue/no-v-html -->
                        <div
                            class="cta-block__item-text"
                            v-html="item.text"
                        ></div>
                    </li>
                </ul>

                <ds-atom-button
                    v-if="!props.buttonHide"
                    full-width
                    class="cta-block__main__button"
                    :enable-underline="!props.microcopyHide"
                    :underline-text="props.microcopyText"
                    :underline-icon="props.microcopyIcon"
                    size="medium"
                    @click="scrollToSelector('.content', -100 )"
                >
                    <template #text="{ classes }">
                        <WatsonAtomDynamicText
                            :text="props.button"
                            :class="classes"
                            :typography="props.typography.button"
                        />
                    </template>
                </ds-atom-button>
            </div>

            <div class="cta-block__contact">
                <WatsonAtomImage
                    v-if="props.contactImage.url"
                    :content="props.contactImage"
                    class="cta-block__contact-image"
                />

                <div
                    v-if="props.contactHeading || props.contactText"
                    class="cta-block__contact-content"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        v-if="props.contactHeading"
                        class="cta-block__contact-heading u-heading-6 u-bold"
                        v-html="props.contactHeading"
                    ></div>
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        v-if="props.contactText"
                        class="cta-block__contact-text"
                        v-html="props.contactText"
                    ></div>
                </div>

                <ds-atom-button
                    v-if="props.contactButton"
                    button-type="secondary"
                    component="a"
                    :href="`tel:${props.contactButton}`"
                    full-width
                    size="small"
                    :icon-left="props.contactButtonIcon || ''"
                    icon-type="solid"
                >
                    <template #text="{ classes }">
                        <WatsonAtomDynamicText
                            :text="props.contactButton"
                            :class="classes"
                            :typography="props.typography.contactButton"
                        />
                    </template>
                </ds-atom-button>
            </div>
        </div>
    </article>
</template>

<script setup>
import { computed } from 'vue';
import { DsAtomButton } from '@leads.io/design-system-component-library/marketplaces';
import WatsonAtomImage from '@atoms/image/watson-atom-image.vue';
import WatsonAtomDynamicText from '@atoms/dynamic-text/watson-atom-dynamic-text.vue';
import { scrollToSelector } from '../../../../js/utils/scroll-to-selector';

const props = defineProps({
    content: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    heading: {
        type: String,
        required: true,
    },
    subheading: {
        type: String,
        required: true,
    },
    items: {
        type: Array,
        required: true,
    },
    buttonHide: {
        type: Boolean,
        required: true,
    },
    button: {
        type: String,
        required: true,
    },
    microcopyHide: {
        type: Boolean,
        required: true,
    },
    microcopyIcon: {
        type: String,
        required: false,
        default: null,
    },
    microcopyText: {
        type: String,
        required: true,
    },
    contactImage: {
        type: [Object, Boolean],
        required: false,
        default: null,
    },
    contactHeading: {
        type: String,
        required: true,
    },
    contactText: {
        type: String,
        required: true,
    },
    contactButton: {
        type: String,
        required: true,
    },
    contactButtonIcon: {
        type: String,
        required: false,
        default: null,
    },
    shadow: {
        type: Boolean,
        required: true,
    },
    background: {
        type: String,
        required: false,
        default: null,
    },
    contactBackground: {
        type: String,
        required: false,
        default: null,
    },
    contentColor: {
        type: String,
        required: false,
        default: null,
    },
    contactTextColor: {
        type: String,
        required: false,
        default: null,
    },
    typography: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const style = computed(() => ({
    '--background': props.background || 'initial',
    '--contact-background': props.contactBackground || 'initial',
    '--content-color': props.contentColor || 'initial',
    '--contact-text-color': props.contactTextColor || 'initial',
}));
</script>

<style lang="scss" scoped>
@use 'watson-theme-cta-block';
</style>
